import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AdminAuthState {
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  /** Authenticator Assurance Level: 'aal1' = password only, 'aal2' = 2FA passed. */
  aal: "aal1" | "aal2" | null;
  /** True when the account has at least one verified TOTP factor. */
  hasFactor: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthState | undefined>(undefined);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [aal, setAal] = useState<"aal1" | "aal2" | null>(null);
  const [hasFactor, setHasFactor] = useState(false);

  const evaluate = async (current: Session | null) => {
    if (!current) {
      setIsAdmin(false);
      setAal(null);
      setHasFactor(false);
      return;
    }

    // Assurance level (did the user pass 2FA this session?)
    const { data: aalData } =
      await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setAal((aalData?.currentLevel as "aal1" | "aal2") ?? "aal1");

    // Verified TOTP factors
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    setHasFactor((factorsData?.totp?.length ?? 0) > 0);

    // Admin role check
    const { data: roleData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", current.user.id)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!roleData);
  };

  const bootstrap = async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    await evaluate(data.session);
    setLoading(false);
  };

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      // Defer Supabase calls to avoid deadlocks inside the callback.
      setTimeout(() => {
        void evaluate(next);
      }, 0);
    });

    void bootstrap();
    return () => sub.subscription.unsubscribe();
  }, []);

  const refresh = async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    await evaluate(data.session);
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setIsAdmin(false);
    setAal(null);
    setHasFactor(false);
  };

  return (
    <AdminAuthContext.Provider
      value={{ session, loading, isAdmin, aal, hasFactor, refresh, signOut }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
